<template>
  <q-card
    class="q-ma-sm noselect"
    :class="{ 'current-session-program': isCurrent, 'any-session-program': !isCurrent, 'hidden-program': isHidden }"
    @dblclick="onDoubleClick"
    @contextmenu="onDoubleClick"
  >
    <q-card-section
      class="q-pt-none q-pb-none q-pr-xs text-overline
            text-uppercase text-grey-6 program-overline row"
    >
      <div class="col">
        <q-icon
          v-if="isHidden"
          name="visibility_off"
        />
        {{ $t('sessions.sessionProgramCard.textProgram') }}
      </div>
      <q-space />
      <div
        v-if="!hideMenu"
        class="col-auto row"
      >
        <div class="row">
          <div>
            <q-btn
              v-if="sessionProgram.pinned"
              flat
              round
              dense
              color="negative"
              icon="push_pin"
              size="sm"
              @click="onUnpin"
            >
              <q-tooltip>
                {{ $t('sessions.sessionProgramCard.textItemUnpin') }}
              </q-tooltip>
            </q-btn>
          </div>
          <div>
            <q-btn
              v-if="canPin"
              flat
              round
              dense
              icon="push_pin"
              size="sm"
              @click="onPinToTop"
            >
              <q-tooltip>
                {{ $t('sessions.sessionProgramCard.textItemPin') }}
              </q-tooltip>
            </q-btn>
          </div>
          <div>
            <q-btn
              flat
              round
              dense
              icon="more_vert"
              size="sm"
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <q-item
                    v-if="sessionProgram.hidden"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onUnhide"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="visibility"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionProgramCard.textItemUnhide') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canHide"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onHide"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="visibility_off"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionProgramCard.textItemHide') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="showMoveTo"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onMoveTo"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="swap_horiz"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionProgramCard.textItemMove') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onRemove"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="clear"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionProgramCard.textItemRemove') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>
    </q-card-section>
    <q-card-section
      class="program-name ellipsis q-pt-none q-pb-none"
      :class="{ 'text-grey-7': isHidden }"
    >
      <behavior-tracking-icon v-if="isBehaviorTracking" />
      {{ sessionProgram.programName }}
    </q-card-section>
    <q-card-section class="ellipsis">
      <q-chip
        v-if="showWaitingTargets"
        v-model:selected="waitingTargetsSelected"
        size="sm"
        :label="waitingTargets"
        :icon="targetStatuses.waiting.icon"
        :color="targetStatuses.waiting.color"
        text-color="white"
        clickable
        :outline="!waitingTargetsSelected"
      >
        <q-tooltip>
          <span v-if="sessionProgram.selectedTargets.waiting">
            {{ $t('sessions.sessionProgramCard.textWaitingTargetsSelected', { waitingTargets }) }}
          </span>
          <span v-else>
            {{ $t('sessions.sessionProgramCard.textWaitingTargetsNotSelected') }}
          </span>
        </q-tooltip>
      </q-chip>
      <q-chip
        v-model:selected="probeTargetsSelected"
        size="sm"
        :icon="targetStatuses.probe.icon"
        :color="targetStatuses.probe.color"
        :label="probeTargets"
        text-color="white"
        clickable
        :outline="!probeTargetsSelected"
      >
        <q-tooltip>
          <span v-if="sessionProgram.selectedTargets.probe">
            {{ $t('sessions.sessionProgramCard.textProbeTargetsSelected', { probeTargets }) }}
          </span>
          <span v-else>
            {{ $t('sessions.sessionProgramCard.textProbeTargetsNotSelected') }}
          </span>
        </q-tooltip>
      </q-chip>
      <q-chip
        v-model:selected="acquisitionTargetsSelected"
        size="sm"
        :icon="targetStatuses.acquisition.icon"
        :color="targetStatuses.acquisition.color"
        :label="acquisitionTargets"
        text-color="white"
        clickable
        :outline="!acquisitionTargetsSelected"
      >
        <q-tooltip>
          <span v-if="sessionProgram.selectedTargets.acquisition">
            {{ $t(
              'sessions.sessionProgramCard.textAcquisitionTargetsSelected',
              { acquisitionTargets }
            ) }}
          </span>
          <span v-else>
            {{ $t('sessions.sessionProgramCard.textAcquisitionTargetsNotSelected') }}
          </span>
        </q-tooltip>
      </q-chip>
      <q-chip
        v-model:selected="masteredTargetsSelected"
        size="sm"
        :icon="targetStatuses.mastered.icon"
        :color="targetStatuses.mastered.color"
        :label="masteredTargets"
        text-color="white"
        clickable
        :outline="!masteredTargetsSelected"
      >
        <q-tooltip>
          <span v-if="sessionProgram.selectedTargets.mastered">
            {{ $t(
              'sessions.sessionProgramCard.textMasteredTargetsSelected',
              { masteredTargets }
            ) }}
          </span>
          <span v-else>
            {{ $t('sessions.sessionProgramCard.textMasteredTargetsNotSelected') }}
          </span>
        </q-tooltip>
      </q-chip>
    </q-card-section>
  </q-card>
</template>
<script>
import { targetStatuses } from 'boot/globals';
import BehaviorTrackingIcon from 'components/Programs/BehaviorTrackingIcon';
import { get, isEmpty } from 'lodash';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';

export default {
  name: 'SessionProgramCard',

  components: { BehaviorTrackingIcon },

  props: {
    sessionProgram: {
      type: Object,
      required: true,
    },
    isCurrent: {
      type: Boolean,
      default: false,
    },
    pinAvailable: {
      type: Boolean,
      default: true,
    },
    hideMenu: {
      type: Boolean,
      default: false,
    },
    showMoveTo: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: null,
    },
  },

  emits: ['pin', 'unpin', 'hide', 'unhide', 'remove', 'move-to', 'doubleclick', 'change-target-status-selected'],

  computed: {

    isBehaviorTracking() {
      return isBehaviorTrackingProgram(this.sessionProgram.program);
    }, // isBehaviorTracking()

    showWaitingTargets() {
      return ['taskAnalysis'].includes(get(this.sessionProgram, ['program', 'type']));
    }, // showWaitingTargets()

    isHidden() {
      return this.sessionProgram.hidden
        || (!ACTIVE_PROGRAM_STATUSES.includes(get(this.sessionProgram, ['program', 'status'])));
    }, // isHidden()

    canPin() {
      return !this.sessionProgram.pinned
        && this.pinAvailable
        && !this.isHidden;
    },

    canHide() {
      return !this.sessionProgram.pinned
        && !this.isHidden;
    }, // canHide()

    targetStatuses() {
      return targetStatuses;
    }, // targetStatuses()

    waitingTargets() {
      return this.targetsInStatus('waiting');
    }, // waitingTargets()

    probeTargets() {
      return this.targetsInStatus('probe');
    }, // probeTargets()

    acquisitionTargets() {
      return this.targetsInStatus('acquisition');
    }, // acquisitionTargets()

    masteredTargets() {
      return this.targetsInStatus('mastered');
    }, // masteredTargets(),

    waitingTargetsSelected: {
      get() {
        return this.targetsInStatusIsSelected('waiting');
      },
      set(value) {
        this.setTargetsInStatusSelected('waiting', value);
      },
    }, // waitingTargetsSelected()

    probeTargetsSelected: {
      get() {
        return this.targetsInStatusIsSelected('probe');
      },
      set(value) {
        this.setTargetsInStatusSelected('probe', value);
      },
    }, // probeTargetsSelected()

    acquisitionTargetsSelected: {
      get() {
        return this.targetsInStatusIsSelected('acquisition');
      },
      set(value) {
        this.setTargetsInStatusSelected('acquisition', value);
      },
    }, // acquisitionTargetsSelected()

    masteredTargetsSelected: {
      get() {
        return this.targetsInStatusIsSelected('mastered');
      },
      set(value) {
        this.setTargetsInStatusSelected('mastered', value);
      },
    }, // masteredTargetsSelected()
  },

  methods: {

    onPinToTop() {
      this.$emit('pin', this.index);
    }, // onPinToTop()

    onUnpin() {
      this.$emit('unpin', this.index);
    }, // onUnpin()

    onHide() {
      this.$emit('hide', this.index);
    }, // onHide()

    onUnhide() {
      this.$emit('unhide', this.index);
    }, // onUnhide()

    onRemove() {
      this.$emit('remove');
    },

    onMoveTo() {
      this.$emit('move-to');
    }, // onMoveTo()

    onDoubleClick(e) {
      this.$emit('doubleclick');
      e.preventDefault();
    },

    /**
     * Returns the number of targets in a specific status
     */
    targetsInStatus(targetStatus) {
      if (isEmpty(this.sessionProgram.program.targetsPerStatus)) {
        return 0;
      }

      const statusObj = this.sessionProgram.program.targetsPerStatus.find(
        (statusCount) => statusCount.status === targetStatus,
      );

      return get(statusObj, 'count', 0);
    }, // targetsInStatus()

    /**
     * Are targets in the specified status selected?
     */
    targetsInStatusIsSelected(targetStatus) {
      if (isEmpty(this.sessionProgram.selectedTargets)) {
        return false;
      }

      return get(this.sessionProgram.selectedTargets, targetStatus, false);
    }, // targetsInStatusIsSelected()

    /**
     * Change whether targets in the specified status are selected
     */
    setTargetsInStatusSelected(targetStatus, value) {
      this.$emit('change-target-status-selected', { sessionProgram: this.sessionProgram, targetStatus, value });
    }, // targetsInStatusIsSelected()

  },
};
</script>
<style lang="scss" scoped>
.hidden-program {
  background-color: $grey-2;
}

.program-overline {
  font-size: 7px;
}

.any-session-program {
  width: 250px;
}

.current-session-program {
  background-color: $green-2;
  width: 250px;
}

.current-session-program .program-name {
  font-weight: bold;
}
</style>
